/*
 * Lazy, size-aware video loading.
 *
 * <video data-video-lazy> elements are rendered without a source (see
 * snippets/video-lazy.liquid). This script attaches the smallest Shopify
 * rendition that covers the element when it comes near the viewport, and
 * also whenever play() is called (Alpine videoToggler or a user click), so
 * playback behaviour is unchanged while the page load carries no video bytes.
 */
(function () {
  'use strict';

  if (!('HTMLMediaElement' in window)) {
    return;
  }

  var SELECTOR = 'video[data-video-lazy]';
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var saveData = !!(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));
  var isPhone = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;

  function parseSources(video) {
    if (video.__lazySources) {
      return video.__lazySources;
    }

    var list = [];

    try {
      list = JSON.parse(video.getAttribute('data-sources') || '[]');
    } catch (error) {
      list = [];
    }

    list = list.filter(function (source) {
      return source && source.url && /mp4/i.test(source.mime || source.format || '');
    });

    list = expandRenditions(list);

    list.sort(function (a, b) {
      return (a.h || 0) - (b.h || 0) || (a.w || 0) - (b.w || 0);
    });

    video.__lazySources = list;
    return list;
  }

  // Liquid's video.sources only lists the top MP4 rendition, but Shopify's CDN
  // also stores the 720p and 480p encodes of the same file under a predictable
  // name. Add them as candidates; a missing file falls back to the next size up
  // (see the error handler in useSource()).
  var LADDER = [
    { key: 'HD-1080p-7.2Mbps', height: 1080 },
    { key: 'HD-720p-4.5Mbps', height: 720 },
    { key: 'SD-480p-1.5Mbps', height: 480 },
  ];

  function expandRenditions(list) {
    var out = [];
    var seen = {};

    list.forEach(function (source) {
      var match = source.url.match(/\.(HD-1080p-7\.2Mbps|HD-720p-4\.5Mbps|SD-480p-1\.5Mbps)-(\d+)\.mp4/);

      if (!seen[source.url]) {
        seen[source.url] = true;
        out.push(source);
      }

      if (!match) {
        return;
      }

      var start = -1;

      LADDER.forEach(function (step, index) {
        if (step.key === match[1]) {
          start = index;
        }
      });

      if (!source.h) {
        source.h = LADDER[start].height;
      }

      for (var index = start + 1; index < LADDER.length; index += 1) {
        var step = LADDER[index];
        var scale = step.height / LADDER[start].height;
        var url = source.url.replace('.' + match[1] + '-', '.' + step.key + '-');

        if (!seen[url]) {
          seen[url] = true;
          out.push({
            url: url,
            format: 'mp4',
            mime: source.mime,
            w: source.w ? Math.round(source.w * scale) : 0,
            h: Math.round(source.h * scale),
            derived: true,
            fallback: source,
          });
        }
      }
    });

    return out;
  }

  function pickSource(video) {
    var sources = parseSources(video);

    if (!sources.length) {
      return null;
    }

    if (saveData) {
      return sources[0];
    }

    var candidates = sources;

    if (isPhone && isPhone.matches) {
      var capped = sources.filter(function (source) {
        return Math.max(source.w || 0, source.h || 0) <= 720;
      });

      if (capped.length) {
        candidates = capped;
      }
    }

    var rect = video.getBoundingClientRect();
    var width = rect.width || video.clientWidth || 0;
    var height = rect.height || video.clientHeight || 0;

    if (!width || !height) {
      return candidates[Math.min(candidates.length - 1, Math.floor(candidates.length / 2))];
    }

    // Video tolerates slight upscaling far better than stills: cap the pixel
    // ratio at 1.5 and accept a rendition that covers 90% of the box.
    var ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    var needWidth = width * ratio * 0.9;
    var needHeight = height * ratio * 0.9;

    for (var index = 0; index < candidates.length; index += 1) {
      var source = candidates[index];

      if ((!source.w || source.w >= needWidth) && (!source.h || source.h >= needHeight)) {
        return source;
      }
    }

    return candidates[candidates.length - 1];
  }

  function markReady(video) {
    video.classList.add('is-ready');
  }

  function attach(video, mode) {
    if (video.__lazyAttached) {
      return;
    }

    video.__lazyAttached = true;

    var source = pickSource(video);

    if (!source) {
      markReady(video);
      return;
    }

    var poster = video.previousElementSibling;

    if (poster && poster.classList && poster.classList.contains('video-lazy__poster')) {
      var posterSrc = poster.currentSrc || poster.src;

      if (posterSrc) {
        video.setAttribute('poster', posterSrc);
      }
    }

    useSource(video, source, mode);
  }

  function useSource(video, source, mode) {
    video.setAttribute('data-video-attached', source.h ? String(source.h) + 'p' : 'true');
    video.preload = mode === 'auto' ? 'auto' : 'metadata';

    if (source.derived) {
      var onError = function () {
        video.removeEventListener('error', onError);

        if (video.getAttribute('src') !== source.url) {
          return;
        }

        var wasPlaying = !video.paused;
        useSource(video, source.fallback, mode);

        if (wasPlaying) {
          nativePlay.call(video).catch(function () {});
        }
      };

      video.addEventListener('error', onError);
    }

    video.src = source.url;

    if (video.readyState >= 2) {
      markReady(video);
    }
  }

  var nativePlay = HTMLMediaElement.prototype.play;

  HTMLMediaElement.prototype.play = function () {
    if (this.hasAttribute && this.hasAttribute('data-video-lazy') && !this.__lazyAttached) {
      attach(this, 'auto');
    }

    return nativePlay.apply(this, arguments);
  };

  var observers = {};

  function observerFor(autoplay) {
    var key = autoplay ? 'auto' : 'metadata';

    if (observers[key]) {
      return observers[key];
    }

    if (!('IntersectionObserver' in window)) {
      return null;
    }

    observers[key] = new IntersectionObserver(
      function (entries) {
        for (var index = 0; index < entries.length; index += 1) {
          var entry = entries[index];

          if (!entry.isIntersecting) {
            continue;
          }

          observers[key].unobserve(entry.target);

          if (!saveData) {
            attach(entry.target, 'metadata');
          }
        }
      },
      { rootMargin: autoplay ? '50% 0px' : '0px 0px' },
    );

    return observers[key];
  }

  // Prefetch (metadata + first frame) starts only once the page has finished
  // loading, so the initial load never competes with video bytes. Until then a
  // play() call still attaches a source immediately.
  var pageLoaded = document.readyState === 'complete';
  var pending = [];

  function observe(video) {
    var observer = observerFor(video.getAttribute('data-autoplay') === 'true');

    if (observer) {
      observer.observe(video);
    } else {
      attach(video, 'metadata');
    }
  }

  function flushPending() {
    pageLoaded = true;

    var list = pending;
    pending = [];

    for (var index = 0; index < list.length; index += 1) {
      observe(list[index]);
    }
  }

  if (!pageLoaded) {
    window.addEventListener('load', function () {
      window.setTimeout(flushPending, 800);
    });
  }

  function register(video) {
    if (video.__lazyRegistered) {
      return;
    }

    video.__lazyRegistered = true;

    // Clones (for example Splide loop slides) copy the attached src; reset them
    // so they go through the same lazy path.
    if (video.hasAttribute('src') && !video.__lazyAttached) {
      video.removeAttribute('src');
      video.removeAttribute('data-video-attached');
      video.classList.remove('is-ready');
      video.preload = 'none';
      video.load();
    }

    video.addEventListener('loadeddata', function () {
      markReady(video);
    });

    video.addEventListener('playing', function () {
      markReady(video);
    });

    if (pageLoaded) {
      observe(video);
    } else {
      pending.push(video);
    }
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;

    if (scope !== document && scope.matches && scope.matches(SELECTOR)) {
      register(scope);
    }

    var nodes = scope.querySelectorAll(SELECTOR);

    for (var index = 0; index < nodes.length; index += 1) {
      register(nodes[index]);
    }
  }

  scan(document);

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var index = 0; index < mutations.length; index += 1) {
        var added = mutations[index].addedNodes;

        for (var position = 0; position < added.length; position += 1) {
          if (added[position].nodeType === 1) {
            scan(added[position]);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
